<template>
  <div>
    <div
      class="w-64 flex flex-col bg-white rounded-md relative overflow-hidden image-container cursor-pointer"
      :style="`box-shadow: 3px 3px 5px 3px ${tintColor}`"
      @click="handleClick()"
    >
      <div v-if="attachment.owner?.logo" class="flex justify-center py-2">
        <img :src="attachment.owner.logo.url" alt="owner logo" class="h-8">
      </div>
      <div class="flex h-64">
        <base64-image
          v-if="attachment.has_thumbnail && thumbnail"
          alt="Image thumbnail"
          class="flex flex-1 object-contain"
          :base64="thumbnail"
        />
        <div
          v-else
          class="flex flex-1 items-center justify-center"
        >
          <img
            src="@/assets/images/logos/apodis.svg"
            alt="Default attachment thumbnail"
            class="object-contain w-32"
          >
        </div>
      </div>
      <div class="flex flex-row items-center justify-between overflow-hidden p-4">
        <span
          class="font-medium line-clamp-3"
          :style="`color: ${textColor ?? '#3513e2'}`"
        >
          {{ attachment.title }}
        </span>
        <div class="flex justify-end pl-2">
          <icon name="mdi-eye" :color="iconColor ?? 'primary'" />
        </div>
      </div>
    </div>
    <div v-if="attachment.related_sale" class="mt-2 w-64 flex flex-1 justify-center">
      <v-chip color="primary" variant="flat" prepend-icon="mdi-pill" density="compact">
        Patient sous {{ attachment.related_sale.item.name.split(' ').slice(0, 2).join(' ') }}
      </v-chip>
    </div>
    <AttachmentDialog
      v-model:open="showModal"
      :attachment="attachment"
      :is-prefillable="isPrefillable"
      :consumer-key="consumer?.consumer_key ?? customer?.consumer_key"
    />
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {attachmentApi} from '@/container';
import Base64Image from '@/core/components/base-64-image/Base64Image.vue';
import ImageService from "@/core/services/ImageService";
import Icon from "@/core/components/icon/Icon.vue";
import {Attachment} from "@/attachment/interfaces/attachment";
import {usePatientStore} from "@/stores/patient.store";
import {storeToRefs} from "pinia";
import AttachmentDialog from "@/attachment/components/AttachmentDialog.vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

const {attachment, tintColor = '#00000030', consumer} = defineProps<{
    attachment: Attachment;
    tintColor: string;
    textColor?: string;
    iconColor?: string;
    consumer?: Customer | Beneficiary;
}>();

const thumbnail = ref<string | null>(null);
const showModal = ref(false);
const {attachments, customer} = storeToRefs(usePatientStore());

/**
 * On mount fetch the thumbnail if it exists.
 */
onMounted(() => {
    if (attachment.has_thumbnail) {
        attachmentApi().thumbnail(attachment.id)
            .then(response => {
                thumbnail.value = ImageService().arrayBufferToBase64(response);
            });
    }
});

const isPrefillable = computed(() => {
    const attachmentIds = attachments.value.map((attachment) => attachment.id);

    return attachmentIds.includes(attachment.id);
});

/**
 * Handle card click.
 */
const handleClick = () => {
    showModal.value = true;
};
</script>

<style scoped>
.image-container:hover .overlay {
    bottom: 0;
}

.image-container img {
    transition: transform .5s ease;
}

.image-container:hover img {
    transform: scale(1.05);
}
</style>
